import React, { useEffect, useState } from 'react';
import { motion, useMotionValue, useTransform, animate, AnimatePresence } from 'framer-motion';
import { PhoneIcon, GlobeIcon, ShieldCheckIcon, LayoutDashboardIcon, UploadCloudIcon, FileTextIcon, CheckCircle2Icon, SearchIcon, TvIcon, CameraIcon, LockIcon, ZapIcon, TrendingDownIcon, SmileIcon, ClockIcon, FileXIcon, MapPinIcon, CalendarIcon, ServerIcon, ActivityIcon, ScaleIcon, ConstructionIcon, TruckIcon, ChevronDownIcon, ArrowRightIcon } from 'lucide-react';
import { ServiceType } from './QuoteModal';
export function HeroSection({
  onOpenQuote,
  onServiceClick,
  onNavigate
}: {
  onOpenQuote?: () => void;
  onServiceClick?: (type: ServiceType) => void;
  onNavigate?: (page: string) => void;
}) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const slides = [{
    title: 'Stop Overpaying for Business Tech',
    subtitle: 'Our AI-driven audit uncovers hidden fees and unused services in minutes.',
    image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1600&h=800&fit=crop',
    label: 'Audit & Savings'
  }, {
    title: 'Seamless Business Relocation',
    subtitle: 'Transfer your entire IT infrastructure with zero downtime and expert coordination.',
    image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1600&h=800&fit=crop',
    label: 'Moving & Relocation'
  }, {
    title: 'Enterprise-Grade New Installs',
    subtitle: 'From fiber internet to custom CRM systems, we build your digital foundation.',
    image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=1600&h=800&fit=crop',
    label: 'New Installation'
  }];
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);
  return <>
      <section className="relative min-h-[70vh] flex flex-col overflow-hidden bg-slate-900" data-id="element-149">
        {/* Carousel Background */}
        <AnimatePresence mode="wait" data-id="element-150">
          <motion.div key={currentSlide} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 1
        }} className="absolute inset-0 z-0" data-id="element-151">
            <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/60 to-transparent z-10" data-id="element-152" />
            <img src={slides[currentSlide].image} alt={slides[currentSlide].title} className="w-full h-full object-cover opacity-50" data-id="element-153" />
          </motion.div>
        </AnimatePresence>

        {/* Top Utility Bar */}
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full" data-id="element-154">
          <div className="flex items-center justify-between py-8 border-b border-white/10" data-id="element-155">
            <div className="text-2xl font-black tracking-[0.15em] text-white" data-id="element-156">
              AMBIENT TELECOMS
            </div>
            <div className="hidden md:flex items-center gap-8 text-[10px] font-bold text-white/50 uppercase tracking-[0.2em]" data-id="element-157">
              <div className="flex items-center gap-2" data-id="element-158">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" data-id="element-159"></span>
                <span data-id="element-160">Systems: Active</span>
              </div>
              <span data-id="element-161">v4.0.2</span>
            </div>
          </div>
        </div>

        {/* Hero Content */}
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full flex-1 flex flex-col justify-center py-20" data-id="element-162">
          <div className="max-w-3xl" data-id="element-163">
            <motion.div key={currentSlide} initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6
          }} data-id="element-164">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-[#22A39F]/20 border border-[#22A39F]/30 text-[#22A39F] text-xs font-bold mb-6 backdrop-blur-sm" data-id="element-165">
                {slides[currentSlide].label}
              </div>
              <h1 className="text-5xl md:text-7xl font-black text-white leading-[1.05] mb-6 tracking-tight" data-id="element-166">
                {slides[currentSlide].title}
              </h1>
              <p className="text-xl text-slate-300 leading-relaxed mb-10 max-w-2xl" data-id="element-167">
                {slides[currentSlide].subtitle}
              </p>
              <div className="flex gap-4" data-id="element-168">
                <button onClick={() => {
                document.getElementById('service-ribbon')?.scrollIntoView({
                  behavior: 'smooth'
                });
              }} className="bg-[#22A39F] hover:bg-[#1d8a87] text-white px-8 py-4 rounded-xl font-bold text-lg transition-all shadow-lg shadow-[#22A39F]/20" data-id="element-169">
                  Get Started
                </button>
                <div className="flex gap-2 items-center" data-id="element-170">
                  {slides.map((_, i) => <button key={i} onClick={() => setCurrentSlide(i)} className={`w-12 h-1.5 rounded-full transition-all ${currentSlide === i ? 'bg-[#22A39F]' : 'bg-white/20'}`} data-id="element-171" />)}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Sticky Ribbon */}
      <div id="service-ribbon" className="z-40 bg-white/95 backdrop-blur-xl border-y border-slate-200 shadow-xl scroll-mt-32" data-id="element-172">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-173">
          <div className="flex flex-col lg:flex-row items-stretch lg:items-center py-2 lg:py-0" data-id="element-174">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 divide-y sm:divide-y-0 lg:divide-x divide-slate-100 flex-1" data-id="element-175">
              {/* Card 1: Internet Audit */}
              <motion.div whileHover={{
              backgroundColor: 'rgba(248, 250, 252, 1)'
            }} className="py-8 px-6 flex flex-col items-center text-center gap-4 group cursor-pointer transition-colors" onClick={() => onServiceClick?.('internet-audit')} data-id="element-176">
                <div className="w-12 h-12 bg-[#22A39F]/10 rounded-2xl flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform shadow-sm" data-id="element-177">
                  <ScaleIcon className="w-6 h-6 text-[#22A39F]" data-id="element-178" />
                </div>
                <div className="min-w-0" data-id="element-179">
                  <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em] mb-1 truncate" data-id="element-180">
                    Audit & Savings
                  </h3>
                  <div className="flex items-center justify-center gap-1.5 text-[#011B33] text-sm font-bold leading-tight" data-id="element-181">
                    Lower my{' '}
                    <span className="text-[#22A39F]" data-id="element-182">Internet Bill</span>
                    <ChevronDownIcon className="w-3.5 h-3.5 text-slate-300 group-hover:text-[#22A39F] transition-colors" data-id="element-183" />
                  </div>
                </div>
              </motion.div>

              {/* Card 2: TV Audit */}
              <motion.div whileHover={{
              backgroundColor: 'rgba(248, 250, 252, 1)'
            }} className="py-8 px-6 flex flex-col items-center text-center gap-4 group cursor-pointer transition-colors" onClick={() => onServiceClick?.('tv-audit')} data-id="element-184">
                <div className="w-12 h-12 bg-amber-500/10 rounded-2xl flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform shadow-sm" data-id="element-185">
                  <TvIcon className="w-6 h-6 text-amber-600" data-id="element-186" />
                </div>
                <div className="min-w-0" data-id="element-187">
                  <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em] mb-1 truncate" data-id="element-188">
                    TV Savings
                  </h3>
                  <div className="flex items-center justify-center gap-1.5 text-[#011B33] text-sm font-bold leading-tight" data-id="element-189">
                    Lower my <span className="text-[#22A39F]" data-id="element-190">TV Bill</span>
                    <ChevronDownIcon className="w-3.5 h-3.5 text-slate-300 group-hover:text-[#22A39F] transition-colors" data-id="element-191" />
                  </div>
                </div>
              </motion.div>

              {/* Card 3: Setup */}
              <motion.div whileHover={{
              backgroundColor: 'rgba(248, 250, 252, 1)'
            }} className="py-8 px-6 flex flex-col items-center text-center gap-4 group cursor-pointer transition-colors" onClick={() => onServiceClick?.('new-service')} data-id="element-192">
                <div className="w-12 h-12 bg-[#FFD700]/10 rounded-2xl flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform shadow-sm" data-id="element-193">
                  <ConstructionIcon className="w-6 h-6 text-[#011B33]" data-id="element-194" />
                </div>
                <div className="min-w-0" data-id="element-195">
                  <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em] mb-1 truncate" data-id="element-196">
                    New Installation
                  </h3>
                  <div className="flex items-center justify-center gap-1.5 text-[#011B33] text-sm font-bold leading-tight" data-id="element-197">
                    I need a{' '}
                    <span className="text-[#FFD700] bg-slate-900 px-2 py-0.5 rounded ml-1 whitespace-nowrap text-[11px]" data-id="element-198">
                      New Service
                    </span>
                    <ChevronDownIcon className="w-3.5 h-3.5 text-slate-300 group-hover:text-[#22A39F] transition-colors" data-id="element-199" />
                  </div>
                </div>
              </motion.div>

              {/* Card 4: Relocation */}
              <motion.div whileHover={{
              backgroundColor: 'rgba(248, 250, 252, 1)'
            }} className="py-8 px-6 flex flex-col items-center text-center gap-4 group cursor-pointer transition-colors" onClick={() => onServiceClick?.('relocation')} data-id="element-200">
                <div className="w-12 h-12 bg-[#011B33]/10 rounded-2xl flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform shadow-sm" data-id="element-201">
                  <TruckIcon className="w-6 h-6 text-[#011B33]" data-id="element-202" />
                </div>
                <div className="min-w-0" data-id="element-203">
                  <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em] mb-1 truncate" data-id="element-204">
                    Seamless Relocation
                  </h3>
                  <div className="flex items-center justify-center gap-1.5 text-[#011B33] text-sm font-bold leading-tight" data-id="element-205">
                    Transfer my{' '}
                    <span className="text-[#22A39F]" data-id="element-206">Connection</span>
                    <ChevronDownIcon className="w-3.5 h-3.5 text-slate-300 group-hover:text-[#22A39F] transition-colors" data-id="element-207" />
                  </div>
                </div>
              </motion.div>

              {/* Card 5: Website Transfer */}
              <motion.div whileHover={{
              backgroundColor: 'rgba(248, 250, 252, 1)'
            }} className="py-8 px-6 flex flex-col items-center text-center gap-4 group cursor-pointer transition-colors" onClick={() => onServiceClick?.('website-transfer')} data-id="element-208">
                <div className="w-12 h-12 bg-indigo-50/10 rounded-2xl flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform shadow-sm" data-id="element-209">
                  <GlobeIcon className="w-6 h-6 text-indigo-600" data-id="element-210" />
                </div>
                <div className="min-w-0" data-id="element-211">
                  <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em] mb-1 truncate" data-id="element-212">
                    Website Transfer
                  </h3>
                  <div className="flex items-center justify-center gap-1.5 text-[#011B33] text-sm font-bold leading-tight" data-id="element-213">
                    Transfer my <span className="text-[#22A39F]" data-id="element-214">Website</span>
                    <ChevronDownIcon className="w-3.5 h-3.5 text-slate-300 group-hover:text-[#22A39F] transition-colors" data-id="element-215" />
                  </div>
                </div>
              </motion.div>
            </div>

            {/* CTA Button */}
            <div className="py-6 lg:py-0 lg:pl-8 border-t lg:border-t-0 lg:border-l border-slate-100" data-id="element-216">
              <button onClick={onOpenQuote} className="w-full lg:w-auto bg-[#011B33] hover:bg-[#22A39F] text-white px-8 py-4 rounded-2xl font-bold text-sm transition-all shadow-xl hover:shadow-[#22A39F]/20 flex items-center justify-center gap-3 group whitespace-nowrap" data-id="element-217">
                Get Quote
                <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-218" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </>;
}